import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Headset, Send, CheckCircle2, UserPlus, MessagesSquare, Bot } from "lucide-react";
import { toast } from "sonner";

const TABS = [["", "Active"], ["waiting", "Waiting"], ["live", "Live"], ["bot", "With AI assistant"], ["closed", "Closed"]];
const BADGE = { waiting: "bg-[#fff4e5] text-[#c35500]", live: "bg-[#e8f5e9] text-[#137333]", bot: "bg-[#ebf5fe] text-[#0a6ed1]", closed: "bg-[#f0f1f3] text-[#6a6d70]" };
const ROLE_STYLE = { user: "bg-white", rob: "bg-[#fff4ec]", agent: "ml-auto bg-[#0a6ed1] text-white" };
const ROLE_NAME = { user: "Buyer", rob: "AI assistant", agent: "Agent" };

function ChatList({ chats, sel, onSel }) {
  if (!chats.length) return <div className="p-6 text-center text-sm text-[#6a6d70]">No conversations</div>;
  return chats.map((c) => (
    <button key={c.id} data-testid={`inbox-chat-${c.chat_no}`} onClick={() => onSel(c.id)} className={`w-full border-b border-[#f0f1f3] px-4 py-3 text-left hover:bg-[#f5f6f7] ${sel === c.id ? "bg-[#ebf5fe]" : ""}`}>
      <div className="flex items-center gap-2">
        <span className="flex-1 truncate text-sm font-semibold text-[#161d23]">{c.user_name}</span>
        {c.unread_agent > 0 && <span className="rounded-full bg-[#bb0000] px-1.5 text-[10px] font-bold text-white">{c.unread_agent}</span>}
        <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${BADGE[c.status]}`}>{c.status}</span>
      </div>
      <div className="truncate text-xs text-[#6a6d70]">{c.chat_no} · {c.last}</div>
    </button>
  ));
}

function Thread({ chat, onAction }) {
  const [text, setText] = useState("");
  const end = useRef(null);
  useEffect(() => { end.current?.scrollIntoView(); }, [chat?.messages?.length]);
  if (!chat) return <div className="flex h-full flex-col items-center justify-center gap-2 text-sm text-[#6a6d70]"><MessagesSquare size={32} /> Select a conversation</div>;
  const reply = async (e) => { e.preventDefault(); if (!text.trim()) return; await onAction("reply", { text }); setText(""); };
  const closed = chat.status === "closed";
  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-wrap items-center gap-2 border-b border-[#e5e9f0] px-4 py-3">
        <div className="flex-1"><div className="font-semibold text-[#161d23]">{chat.user_name} <span className="text-xs font-normal text-[#6a6d70]">{chat.user_email}</span></div><div className="text-xs text-[#6a6d70]">{chat.chat_no} · group {chat.customer_group} {chat.agent_name && `· agent ${chat.agent_name}`}</div></div>
        {chat.status === "waiting" && <button data-testid="inbox-claim" onClick={() => onAction("claim")} className="inline-flex items-center gap-1 rounded-md bg-[#0a6ed1] px-3 py-1.5 text-xs font-semibold text-white"><UserPlus size={14} /> Join chat</button>}
        {!closed && <button data-testid="inbox-close" onClick={() => onAction("close")} className="inline-flex items-center gap-1 rounded-md border border-[#c8cfda] px-3 py-1.5 text-xs font-semibold text-[#32363a]"><CheckCircle2 size={14} /> Close</button>}
      </div>
      <div className="flex-1 space-y-2 overflow-y-auto bg-[#f5f6f7] p-4" data-testid="inbox-thread">
        {chat.messages.map((m) => m.role === "system"
          ? <div key={m.id} className="text-center text-[11px] text-[#6a6d70]">{m.text}</div>
          : <div key={m.id} className={`max-w-[75%] rounded-lg px-3 py-2 text-sm shadow-sm ${ROLE_STYLE[m.role]}`}>
              <div className="mb-0.5 flex items-center gap-1 text-[10px] font-semibold opacity-70">{m.role === "rob" && <Bot size={11} />}{m.name || ROLE_NAME[m.role]} · {new Date(m.at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</div>
              <div className="whitespace-pre-wrap">{m.text}</div>
            </div>)}
        <div ref={end} />
      </div>
      {!closed && (
        <form onSubmit={reply} className="flex gap-2 border-t border-[#e5e9f0] p-3">
          <input data-testid="inbox-reply-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Reply to the buyer…" className="flex-1 rounded-md border border-[#c8cfda] px-3 py-2 text-sm outline-none focus:border-[#0a6ed1]" />
          <button data-testid="inbox-reply-send" className="inline-flex items-center gap-1 rounded-md bg-[#0a6ed1] px-4 py-2 text-sm font-semibold text-white"><Send size={14} /> Send</button>
        </form>
      )}
    </div>
  );
}

export default function SupportInbox() {
  const [tab, setTab] = useState("");
  const [data, setData] = useState({ chats: [], counts: {} });
  const [sel, setSel] = useState(null);
  const [chat, setChat] = useState(null);
  const loadList = useCallback(() => api.get(`/support/chats${tab ? `?status=${tab}` : ""}`).then((r) => setData(r.data)).catch(() => {}), [tab]);
  const loadChat = useCallback(() => { if (sel) api.get(`/support/chats/${sel}`).then((r) => setChat(r.data.chat)).catch(() => {}); }, [sel]);
  useEffect(() => { loadList(); const t = setInterval(loadList, 4000); return () => clearInterval(t); }, [loadList]);
  useEffect(() => { loadChat(); const t = setInterval(loadChat, 3000); return () => clearInterval(t); }, [loadChat]);
  const onAction = async (a, body) => {
    try { const r = await api.post(`/support/chats/${sel}/${a}`, body); setChat(r.data.chat); loadList(); }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };
  return (
    <div data-testid="support-inbox" className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#F06600]/15 text-[#F06600]"><Headset size={20} /></div>
        <div><h1 className="text-xl font-semibold text-[#161d23]">Webshop Live Chat</h1><p className="text-sm text-[#6a6d70]">B&amp;Q and ASDA buyers handed over from Ask Rob / Ask Karen. Join a chat to reply in real time.</p></div>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {TABS.map(([k, l]) => (
          <button key={l} data-testid={`inbox-tab-${l.toLowerCase().replace(/ /g, "-")}`} onClick={() => setTab(k)} className={`rounded-full px-3 py-1 text-xs font-semibold ${tab === k ? "bg-[#0a6ed1] text-white" : "bg-white text-[#32363a] border border-[#e5e9f0]"}`}>
            {l}{k && data.counts?.[k] != null ? ` (${data.counts[k]})` : ""}
          </button>
        ))}
      </div>
      <div className="grid overflow-hidden rounded-xl border border-[#e5e9f0] bg-white md:grid-cols-[320px_1fr]" style={{ height: "calc(100vh - 230px)", minHeight: 460 }}>
        <div className={`overflow-y-auto border-r border-[#e5e9f0] ${sel ? "hidden md:block" : ""}`}><ChatList chats={data.chats} sel={sel} onSel={setSel} /></div>
        <div className={`${sel ? "" : "hidden md:block"} min-h-0`}>
          {sel && <button onClick={() => { setSel(null); setChat(null); }} className="px-4 pt-2 text-xs text-[#0a6ed1] md:hidden">← All chats</button>}
          <Thread chat={chat} onAction={onAction} />
        </div>
      </div>
    </div>
  );
}
